import axios, { AxiosError } from "axios";
import { useQuery, UseQueryResult } from "react-query";
import { ShiftSiteAttendanceResponse } from "../model/ShiftSite";
import { ShiftSiteAttendanceRequest } from "../request/ShiftSiteAttendanceRequest";
import { HttpClientError } from "../../../../providers/HttpClient";

const ShiftSiteAttendanceApiService = async (
  request: Omit<ShiftSiteAttendanceRequest, "enabled">
): Promise<ShiftSiteAttendanceResponse> => {
  try {
    const { data } = await axios.get(
      `/shift-sites/${request.siteId}/attendance/`,
      {
        params: {
          ...(request.date && { date: request.date }),
          ...(request.date_from && { date_from: request.date_from }),
          ...(request.date_to && { date_to: request.date_to }),
        },
      }
    );
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = HttpClientError(axiosError);
    const customError = new Error(errorMessage);

    customError.cause = axiosError;
    throw customError;
  }
};

const useShiftSiteAttendanceApiService = (
  request: ShiftSiteAttendanceRequest
): UseQueryResult<ShiftSiteAttendanceResponse, Error | AxiosError> => {
  const { enabled = true, refetchInterval = false, ...queryParams } = request;
  return useQuery<ShiftSiteAttendanceResponse, Error | AxiosError>(
    [
      "ShiftSiteAttendanceApiService",
      queryParams.siteId ?? null,
      queryParams.date ?? null,
      queryParams.date_from ?? null,
      queryParams.date_to ?? null,
    ],
    () => ShiftSiteAttendanceApiService(queryParams),
    {
      keepPreviousData: true,
      enabled: enabled && !!queryParams.siteId,
      // Poll only when the caller opts in (today views). react-query's default
      // refetchIntervalInBackground:false pauses polling when the tab is hidden.
      refetchInterval,
    }
  );
};

export { ShiftSiteAttendanceApiService, useShiftSiteAttendanceApiService };
